import {
  LoginRequest,
  LoginResponse,
  User,
  AuthProvider,
  TokenInfo,
  BackendPermissionsResponse,
  DEFAULT_ALLOWED_CULTURES,
} from '@/types/auth';
import { appConfig, config } from '@/config/appConfig';
import { auditLogger } from './auditLogger';
import { apiClient } from '../api/apiClient';
import { keycloakService, KeycloakUser, KeycloakTokenInfo } from './keycloakService';
import { sessionMonitor } from '@/services/session/sessionMonitor';

function resolveAllowedCultures(user: User | null): string[] {
  if (!user) return [...DEFAULT_ALLOWED_CULTURES];
  const c = user.allowedCulture;
  if (c === undefined || c === null) return [...DEFAULT_ALLOWED_CULTURES];
  return c;
}

function localeMatchesAllowedCulture(locale: string, allowed: string[]): boolean {
  if (!locale) return false;
  if (allowed.includes('*')) return true;
  if (allowed.includes(locale)) return true;
  const primary = locale.split('-')[0];
  for (const a of allowed) {
    if (!a) continue;
    if (a === locale) return true;
    if (a === primary) return true;
    const aPrimary = a.split('-')[0];
    if (aPrimary === primary) return true;
  }
  return false;
}

// JWT Token interfaces
interface JWTPayload {
  sub: string;
  iss: string;
  aud: string | string[];
  exp: number;
  iat: number;
  azp?: string;
  realm_access?: {
    roles: string[];
  };
  resource_access?: {
    [clientId: string]: {
      roles: string[];
    };
  };
  preferred_username?: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
}



class AuthService {
  private tokenKey = 'auth_token';
  private refreshTokenKey = 'refresh_token';
  private userKey = 'current_user';
  private permissionsChangedCallbacks: Array<(userId: string) => void> = [];
  private authClearedCallbacks: Array<() => void> = [];

  constructor() {
    // Set up authentication methods in apiClient to avoid circular dependency
    // These methods now delegate to Keycloak instance for token management
    apiClient.setAuthMethods(
      () => this.getToken(), // Gets token from Keycloak instance
      () => this.refreshToken(), // Refreshes token using Keycloak instance
      () => this.logout() // Logs out using Keycloak instance
    );
  }

  /**
   * Event system to avoid circular dependencies
   */
  
  onPermissionsChanged(callback: (userId: string) => void): void {
    this.permissionsChangedCallbacks.push(callback);
  }

  onAuthCleared(callback: () => void): void {
    this.authClearedCallbacks.push(callback);
  }

  private emitPermissionsChanged(userId: string): void {
    this.permissionsChangedCallbacks.forEach(callback => {
      try {
        callback(userId);
      } catch (error) {
        console.error('Error in permissions changed callback:', error);
      }
    });
  }

  private emitAuthCleared(): void {
    this.authClearedCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in auth cleared callback:', error);
      }
    });
  }

  /**
   * JWT Token parsing utilities
   */
  
  /**
   * Decode JWT token payload
   */
  private decodeJWT(token: string): JWTPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format');
      }
      
      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch (error) {
      console.error('Failed to decode JWT:', error);
      return null;
    }
  }

  /**
   * Validate JWT token expiry and basic claims
   */
  private validateJWT(token: string): boolean {
    const payload = this.decodeJWT(token);
    if (!payload) return false;

    const now = Math.floor(Date.now() / 1000);
    
    // Check expiry
    if (payload.exp && payload.exp < now) {
      console.warn('JWT token has expired');
      return false;
    }

    // Validate issuer (more flexible for mock)
    const keycloakConfig = config.auth.keycloak;
    if (payload.iss) {
      // For mock: issuer is 'mock-keycloak'
      // For production: issuer is 'https://keycloak.example.com/realms/myrealm'
      const isMockIssuer = payload.iss === 'mock-keycloak';
      const isValidIssuer = 
        isMockIssuer || 
        payload.iss === keycloakConfig.issuer ||
        payload.iss.startsWith(keycloakConfig.issuer) ||
        (keycloakConfig.realm && payload.iss.includes(keycloakConfig.realm));
      
      if (!isValidIssuer) {
        console.warn('JWT issuer validation warning:', {
          expected: keycloakConfig.issuer,
          received: payload.iss,
          realm: keycloakConfig.realm
        });
        // Don't fail validation, just warn (backend will do strict validation)
      }
    }

    // Validate audience (more flexible for mock)
    if (payload.aud) {
      const aud = Array.isArray(payload.aud) ? payload.aud : [payload.aud];
      // For mock: audience is 'moneyfellows-admin'
      // For production: audience should match clientId
      const isMockAudience = aud.includes('moneyfellows-admin');
      const isValidAudience = 
        isMockAudience || 
        aud.includes(keycloakConfig.clientId);
      
      if (!isValidAudience) {
        console.warn('JWT audience validation warning:', {
          expected: keycloakConfig.clientId,
          received: aud
        });
        // Don't fail validation, just warn (backend will do strict validation)
      }
    }

    return true;
  }

  /**
   * Extract roles from JWT token
   */
  private extractRolesFromJWT(token: string): string[] {
    const payload = this.decodeJWT(token);
    if (!payload) return [];

    const roles: string[] = [];
    const keycloakConfig = config.auth.keycloak;

    // // Extract realm roles
    // if (payload.realm_access?.roles) {
    //   roles.push(...payload.realm_access.roles);
    // }

    // Extract client-specific roles
    if (payload.resource_access?.[keycloakConfig.clientId]?.roles) {
      roles.push(...payload.resource_access[keycloakConfig.clientId].roles);
    }

    return [...new Set(roles)]; // Remove duplicates
  }

  /**
   * Extract user information from JWT token
   */
  private extractUserFromJWT(token: string): Partial<User> | null {
    const payload = this.decodeJWT(token);
    if (!payload) return null;

    return {
      id: payload.sub,
      email: payload.email || '',
      name: payload.name || payload.preferred_username || '',
      avatar: undefined, // Keycloak doesn't provide avatar in JWT by default
      roles: this.extractRolesFromJWT(token).map(role => ({
        id: role,
        name: role,
        description: '',
        permissions: [],
        countries: ['*'],
        reports: ['*'],
        isSystemRole: true,
      })),
      countries: ['*'], // Will be populated from backend API
      features: [],
      pages: [],
      isActive: true,
    };
  }

  /**
   * OAuth2/Keycloak specific methods
   */

  /**
   * Generate Keycloak authorization URL with PKCE
   */
  // generateKeycloakAuthUrl(): string {
  //   const keycloakConfig = config.auth.keycloak;
    
  //   // Generate PKCE state parameter
  //   const state = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  //   sessionStorage.setItem('oauth2_state', state);
    
  //   const authUrl = new URL(`${keycloakConfig.issuer}/protocol/openid-connect/auth`);
  //   authUrl.searchParams.set('client_id', keycloakConfig.clientId);
  //   authUrl.searchParams.set('redirect_uri', keycloakConfig.redirectUri);
  //   authUrl.searchParams.set('response_type', 'code');
  //   authUrl.searchParams.set('scope', keycloakConfig.scopes.join(' '));
  //   authUrl.searchParams.set('state', state);
    
  //   return authUrl.toString();
  // }

  /**
   * Handle authentication after successful Keycloak login
   * 
   * FLOW:
   * 1. Verify Keycloak is authenticated (assumes already initialized from login redirect)
   * 2. Extract tokens from Keycloak instance
   * 3. Store tokens in localStorage as secure backup
   * 4. Extract user info from token (roles, email, name)
   * 5. Fetch permissions from backend API using Keycloak token
   * 6. Transform backend permissions to menuItems
   * 7. Merge: user + roles + permissions + menuItems
   * 8. Store complete user object in localStorage
   * 
   * This is called from the callback page after successful authentication
   */
  async handleAuthenticationCallback(): Promise<boolean> {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 Step 5: AuthService handling authentication callback...');
      }

      // Step 1: Verify Keycloak is authenticated (should be already initialized from login)
      if (!keycloakService.isAuthenticated()) {
        console.error('❌ Step 5: Keycloak not authenticated in callback');
        return false;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Step 5.1: Keycloak authenticated');
      }

      // Step 2: Extract tokens from Keycloak instance
      const tokenInfo = keycloakService.getTokenInfo();
      if (!tokenInfo) {
        console.error('❌ Step 5: Failed to get token info from Keycloak');
        return false;
      }

      // Step 3: Store tokens in localStorage as secure backup
      this.setToken(tokenInfo.accessToken);
      if (tokenInfo.refreshToken) {
        this.setRefreshToken(tokenInfo.refreshToken);
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Step 5.2: Tokens stored in localStorage (backup)');
      }

      // Step 4: Clear any cached user data to force fresh fetch
      this.clearCachedUserData();

      // Step 5-8: Get complete user information (this handles permission fetch and merge)
      const user = await this.getCurrentUser();
      if (!user) {
        console.error('❌ Step 5: Failed to get user information');
        return false;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Step 5.3: Complete user information retrieved:', {
          id: user.id,
          name: user.name,
          email: user.email,
          roles: user.roles.map(r => r.name),
          pagesCount: user.pages?.length || 0,
        });
      }

      return true;
    } catch (error: any) {
      console.error('❌ Step 5: Authentication callback handling failed:', error);
      return false;
    }
  }

  /**
   * Initialize Keycloak authentication
   * This should only be called on app startup to check for existing authentication
   */
  async initializeKeycloak(): Promise<boolean> {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 AuthService: Initializing Keycloak authentication...');
      }

      const authenticated = await keycloakService.init();
      
      if (authenticated) {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ AuthService: Keycloak authentication successful');
        }
        
        // Get all token information from Keycloak instance
        const tokenInfo = keycloakService.getTokenInfo();
        if (tokenInfo) {
          this.setToken(tokenInfo.accessToken);
          if (tokenInfo.refreshToken) {
            this.setRefreshToken(tokenInfo.refreshToken);
          }
          
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ AuthService: Tokens from Keycloak instance stored successfully');
          }
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('⚠️ AuthService: Keycloak authentication failed');
        }
      }

      return authenticated;
    } catch (error: any) {
      console.error('❌ AuthService: Keycloak initialization failed:', error);
      return false;
    }
  }

  /**
   * Login with Keycloak
   * This method delegates to KeycloakService which handles the complete flow
   */
  async loginWithKeycloak(): Promise<void> {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔑 AuthService: Delegating to KeycloakService for login...');
    }
    
    try {
      // KeycloakService now handles the complete flow:
      // 1. Initialize Keycloak instance
      // 2. Initialize adapter
      // 3. Perform SSO login
      await keycloakService.login();
    } catch (error) {
      console.error('❌ AuthService: Keycloak login failed:', error);
      throw error;
    }
  }

  /**
   * Re-initialize Keycloak with 'login-required' option
   * This forces an immediate redirect to Keycloak SSO login page
   * Use this when error=login_required is received
   */
  async initKeycloakWithLoginRequired(): Promise<void> {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔑 AuthService: Re-initializing Keycloak with login-required...');
    }
    
    try {
      // Delegate to KeycloakService which will:
      // 1. Initialize Keycloak instance if needed
      // 2. Initialize adapter with 'login-required' option
      // 3. Auto-redirect to Keycloak SSO login page
      await keycloakService.initWithLoginRequired();
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ AuthService: Keycloak initialized with login-required');
      }
    } catch (error) {
      console.error('❌ AuthService: Keycloak login-required init failed:', error);
      throw error;
    }
  }

  /**
   * Refresh access token using Keycloak instance
   */
  async refreshAccessToken(): Promise<boolean> {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 AuthService: Refreshing token using Keycloak instance...');
      }

      const refreshed = await keycloakService.refreshToken();
      
      if (refreshed) {
        // Update stored tokens from Keycloak instance
        const tokenInfo = keycloakService.getTokenInfo();
        if (tokenInfo) {
          this.setToken(tokenInfo.accessToken);
          if (tokenInfo.refreshToken) {
            this.setRefreshToken(tokenInfo.refreshToken);
          }
          
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ AuthService: Token refreshed successfully from Keycloak instance');
          }
        }
        
        // Log successful token refresh
        await auditLogger.logUserAction('TOKEN_REFRESH_SUCCESS', {
          timestamp: new Date().toISOString(),
        });
        
        return true;
      }

      console.warn('Token refresh failed');
      return false;
    } catch (error: any) {
      console.error('Token refresh failed:', error);
      
      // Log failed token refresh
      await auditLogger.logUserAction('TOKEN_REFRESH_FAILED', {
        error: error.message,
        timestamp: new Date().toISOString(),
      });
      
      this.clearAuthData();
      return false;
    }
  }

  /**
   * Perform silent token refresh using iframe
   */
  async silentRefresh(): Promise<boolean> {
    try {
      const keycloakConfig = config.auth.keycloak;
      const silentCheckUrl = keycloakConfig.silentCheckUri || `${window.location.origin}/auth/silent-check`;
      
      return new Promise((resolve) => {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = silentCheckUrl;
        
        const handleMessage = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          
          if (event.data.type === 'AUTH_SUCCESS') {
            resolve(true);
          } else if (event.data.type === 'AUTH_ERROR') {
            console.error('Silent refresh failed:', event.data.error);
            resolve(false);
          }
          
          document.body.removeChild(iframe);
          window.removeEventListener('message', handleMessage);
        };
        
        window.addEventListener('message', handleMessage);
        document.body.appendChild(iframe);
        
        // Timeout after 10 seconds
        setTimeout(() => {
          document.body.removeChild(iframe);
          window.removeEventListener('message', handleMessage);
          resolve(false);
        }, 10000);
      });
    } catch (error) {
      console.error('Silent refresh error:', error);
      return false;
    }
  }

  /**
   * Logout from Keycloak (end session)
   */
  async logoutFromKeycloak(): Promise<void> {
    try {
      const keycloakConfig = config.auth.keycloak;
      const token = this.getToken();
      
      if (token) {
        // Call Keycloak logout endpoint
        const logoutUrl = new URL(`${keycloakConfig.issuer}/protocol/openid-connect/logout`);
        logoutUrl.searchParams.set('client_id', keycloakConfig.clientId);
        logoutUrl.searchParams.set('post_logout_redirect_uri', window.location.origin);
        
        // Redirect to Keycloak logout
        window.location.href = logoutUrl.toString();
      }
    } catch (error) {
      console.error('Keycloak logout error:', error);
      // Fallback to local logout
      this.clearAuthData();
    }
  }

  /**
   * Backend Permissions API Integration
   * Simplified: Just fetch raw permissions, no transformation
   */

  /**
   * Fetch user permissions from backend using Keycloak token
   * Returns raw permissions data without transformation
   */
  async fetchUserPermissions(): Promise<BackendPermissionsResponse | null> {
    try {
      // Get token from Keycloak instance
      const token = keycloakService.getTokenSync();
      if (!token) {
        if (process.env.NODE_ENV === 'development') {
          console.log('⚠️ AuthService: No Keycloak token available');
        }
        return null;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('🔑 AuthService: Fetching permissions from backend...');
      }

      // Use Keycloak token for authorization
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const permissionsUrl = `${appConfig.api.apiUrl}/Adminstration/Permissions`;
      
      const response = await apiClient.request<BackendPermissionsResponse>({
        url: permissionsUrl,
        method: 'GET',
        headers,
        skipErrorHandler: false,
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('✅ AuthService: Permissions fetched:', {
          pagesCount: response?.data?.pages?.length || 0
        });
      }

      return response;
    } catch (error: any) {
      console.error('❌ AuthService: Failed to fetch permissions:', error);
      return null;
    }
  }

  /**
   * Cache permissions with TTL
   */
  private setPermissionsCache(permissions: BackendPermissionsResponse | BackendPermissionsResponse): void {
    const cacheKey = `permissions_${this.getToken()?.substring(0, 20)}`;
    const cacheData = {
      data: permissions,
      timestamp: Date.now(),
      ttl: 300000, // 5 minutes default
    };
    
    sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
  }

  /**
   * Get cached permissions
   */
  private getPermissionsCache(): BackendPermissionsResponse | null {
    const cacheKey = `permissions_${this.getToken()?.substring(0, 20)}`;
    
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (!cached) return null;
      
      const cacheData = JSON.parse(cached);
      const now = Date.now();
      
      if (now - cacheData.timestamp > cacheData.ttl) {
        sessionStorage.removeItem(cacheKey);
        return null;
      }
      
      return cacheData.data;
    } catch {
      return null;
    }
  }

  /**
   * Clear permissions cache
   */
  private clearPermissionsCache(): void {
    const cacheKey = `permissions_${this.getToken()?.substring(0, 20)}`;
    sessionStorage.removeItem(cacheKey);
  }


  /**
   * Get available authentication providers
   */
  async getAuthProviders(): Promise<AuthProvider[]> {
    try {
      const response = await apiClient.request<{ data: AuthProvider[] }>({
        url: '/api/auth/providers',
        method: 'GET',
        skipErrorHandler: true,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch auth providers:', error);
      return [];
    }
  }

  /**
   * Authenticate user with specified provider
   */
  async login(loginRequest: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await apiClient.request<LoginResponse>({
        url: '/api/auth/login',
        method: 'POST',
        data: loginRequest,
        skipErrorHandler: true,
      });

      if (response.success && response.token) {
        // Store tokens and user data
        this.setToken(response.token);
        if (response.refreshToken) {
          this.setRefreshToken(response.refreshToken);
        }
        if (response.user) {
          this.setUser(response.user);
        }

        // Log successful login
        await auditLogger.log({
          action: 'LOGIN',
          resource: 'AUTH',
          details: {
            provider: loginRequest.provider,
            userId: response.user?.id,
          },
          success: true,
        });
      }

      return response;
    } catch (error: any) {
      // Log failed login attempt
      await auditLogger.log({
        action: 'LOGIN_FAILED',
        resource: 'AUTH',
        details: {
          provider: loginRequest.provider,
          error: error.message,
        },
        success: false,
      });

      throw error;
    }
  }

  /**
   * Logout user using Keycloak instance
   * This will:
   * 1. Clear localStorage/SessionStorage cached data (but keep Keycloak instance)
   * 2. Call Keycloak logout to terminate server session
   * 3. Keycloak will redirect to login page
   * 4. If logout fails, clear Keycloak instance and redirect manually
   */
  async logout(): Promise<void> {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🚪 AuthService: Initiating logout...');
      }
      
      // ✅ FIRST: Broadcast logout to all other tabs BEFORE clearing state
      // This ensures all tabs log out simultaneously
      sessionMonitor.broadcastLogout();
      
      // Log logout action asynchronously (fire-and-forget, don't block logout)
      const user = this.getCachedUser();
      if (user) {
        // Don't await - audit logging should not block logout
        auditLogger.log({
          action: 'LOGOUT',
          resource: 'AUTH',
          details: {
            userId: user.id,
            multiTab: true,
          },
          success: true,
        }).catch(err => {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Audit log failed (non-blocking):', err);
          }
        });
      }
      
      // ✅ Clear localStorage/SessionStorage (prevents stale data access)
      // But DON'T clear Keycloak instance yet - we need it to call logout
      this.clearLocalStorageData();
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ AuthService: Local storage cleared, now calling Keycloak logout...');
      }
      
      // ✅ Call Keycloak logout to terminate server session
      // This MUST be done before clearing Keycloak instance
      // Keycloak logout will redirect, so code after won't execute if successful
      await keycloakService.logout();
      
      // Note: Code after this won't execute because keycloak.logout() redirects immediately
    } catch (error) {
      console.error('❌ Logout error:', error);
      // Fallback: Clear all data including Keycloak instance and redirect manually
      this.clearAuthData(); // This clears everything including Keycloak instance
      window.location.href = '/user/login';
    }
  }

  /**
   * Clear localStorage and sessionStorage only (doesn't clear Keycloak instance)
   * Used during logout to prevent stale data access while still allowing Keycloak logout
   */
  private clearLocalStorageData(): void {
    if (process.env.NODE_ENV === 'development') {
      console.log('🧹 Clearing localStorage and sessionStorage...');
    }

    // 2️⃣ Clear ALL localStorage (tokens, user, permissions, menu)
    const localStorageKeys = [
      this.tokenKey,           // 'auth_token'
      this.refreshTokenKey,    // 'refresh_token'
      this.userKey,            // 'current_user' (includes permissions, menu, roles)
      'user_permissions',      // Permission cache
      'user_menu',             // Menu cache
      'user_profile',          // Profile data
      'app_settings',          // User settings
    ];
    
    localStorageKeys.forEach(key => localStorage.removeItem(key));
    
    // Clear ANY other auth-related keys
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('auth_') || 
          key.startsWith('user_') || 
          key.startsWith('permission_') ||
          key.startsWith('menu_')) {
        localStorage.removeItem(key);
      }
    });
    
    // 3️⃣ Clear ALL sessionStorage (permissions, oauth, menu, user data)
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('permissions_') ||
          key.startsWith('oauth2_') || 
          key === 'oauth2_state' || 
          key === 'oauth2_redirect_uri' ||
          key.startsWith('menu_') || 
          key.startsWith('navigation_') ||
          key.startsWith('user_') || 
          key.startsWith('current_user_')) {
        sessionStorage.removeItem(key);
      }
    });
    
    // 4️⃣ Clear API response cache from httpClient (memory cache)
    apiClient.clearCache();
    
    // 5️⃣ Clear permissions cache explicitly
    this.clearPermissionsCache();
    
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Local storage and session storage cleared (Keycloak instance preserved)');
    }
  }

  /**
   * Refresh authentication token
   */
  async refreshToken(): Promise<boolean> {
    // Use OAuth2 refresh method
    return this.refreshAccessToken();
  }

  /**
   * Get current user information from Keycloak instance
   * This method handles the complete authentication flow:
   * 1. Check if Keycloak is authenticated
   * 2. Extract all user info from Keycloak token
   * 3. Extract roles from token claims
   * 4. Fetch permissions from backend API
   * 5. Merge all data into final user object
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      // First check if we have cached user data
      const cachedUser = this.getCachedUser();
      if (cachedUser) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔑 AuthService: Using cached user data');
        }
        return cachedUser;
      }

      // Check if Keycloak is already initialized and authenticated
      if (!keycloakService.isAuthenticated()) {
        if (process.env.NODE_ENV === 'development') {
          console.log('⚠️ AuthService: Keycloak not authenticated, cannot get user');
        }
        return null;
      }

      // Get token from Keycloak instance
      const token = keycloakService.getTokenSync();
      if (!token) {
        if (process.env.NODE_ENV === 'development') {
          console.log('⚠️ AuthService: No token from Keycloak instance');
        }
        return null;
      }

      // Extract all user information from Keycloak token
      const userFromToken = this.extractUserFromJWT(token);
      if (!userFromToken) {
        if (process.env.NODE_ENV === 'development') {
          console.log('⚠️ AuthService: Failed to extract user from token');
        }
        return null;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('✅ AuthService: Extracted user from Keycloak token:', {
          id: userFromToken.id,
          name: userFromToken.name,
          email: userFromToken.email,
          roles: userFromToken.roles?.map(r => r.name) || []
        });
      }

      // Get additional user info from Keycloak instance (avatar, etc.)
      const keycloakUser = keycloakService.getUser();
      const userFromKeycloak: User = {
        id: userFromToken.id || keycloakUser?.id || '',
        name: userFromToken.name || keycloakUser?.name || '',
        email: userFromToken.email || keycloakUser?.email || '',
        avatar: keycloakUser?.avatar,
        roles: userFromToken.roles || [],
        countries: [],
        features: [],
        pages: [],
        allowedCulture: [...DEFAULT_ALLOWED_CULTURES],
        isActive: true,
      };

      // Fetch permissions from backend (raw data, no transformation)
      let permissions: BackendPermissionsResponse | null = null;
      
      // Check cache first
      const cachedPermissions = this.getPermissionsCache();
      if (cachedPermissions) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔑 AuthService: Using cached permissions');
        }
        permissions = cachedPermissions;
      } else if (userFromKeycloak.roles.length > 0) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔑 AuthService: Fetching permissions from backend...');
        }
        
        permissions = await this.fetchUserPermissions();
        
        if (permissions) {
          this.setPermissionsCache(permissions);
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ AuthService: Permissions fetched and cached');
          }
        }
      }

      // Extract raw data from backend response - NO TRANSFORMATION
      const pages = (permissions?.data?.pages || []) as any[];
      const countries = permissions?.data?.countries || ['*'];
      const features = permissions?.data?.features || [];
      const rawCulture = permissions?.data?.allowedCulture;
      const allowedCulture =
        rawCulture !== undefined && rawCulture !== null
          ? rawCulture
          : [...DEFAULT_ALLOWED_CULTURES];

      // Store raw backend data directly in user object
      const finalUser: User = {
        ...userFromKeycloak,
        pages: pages, // ✅ Raw pages array from backend
        countries: countries, // ✅ Raw countries from backend
        features: features, // ✅ Raw features from backend
        allowedCulture, // ✅ From Permissions API `allowedCulture`
      };
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ AuthService: User created with RAW backend permissions (no transformation):', {
          id: finalUser.id,
          name: finalUser.name,
          pagesCount: finalUser.pages.length,
          countriesCount: finalUser.countries.length,
          featuresCount: finalUser.features.length,
          allowedCultureCount: finalUser.allowedCulture?.length ?? 0,
          samplePages: finalUser.pages.slice(0, 3).map(p => ({ 
            id: p.identifier, 
            name: p.labels || p.name,
            actionsCount: p.actions?.length || 0 
          })),
        });
      }
      
      this.setUser(finalUser);
      return finalUser;
    } catch (error) {
      console.error('Failed to get current user:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated
   * Priority: Keycloak instance > localStorage token
   */
  isAuthenticated(): boolean {
    // Check Keycloak instance first
    if (keycloakService.isAuthenticated()) {
      return true;
    }
    
    // Fallback: check localStorage for token
    const token = localStorage.getItem(this.tokenKey);
    return !!token;
  }

  /**
   * Get token with fallback
   * Priority: Keycloak instance (source of truth) > localStorage (backup)
   */
  getToken(): string | null {
    // Try Keycloak instance first (source of truth)
    const keycloakToken = keycloakService.getTokenSync();
    if (keycloakToken) {
      return keycloakToken;
    }
    
    // Fallback to localStorage (backup)
    return localStorage.getItem(this.tokenKey);
  }

  /**
   * Set authentication token
   * SECURITY: Only stores plaintext in development for debugging
   * Production uses encrypted storage via tokenEncryptionService
   */
  setToken(token: string): void {
    // Validate token before storing
    if (!this.validateJWT(token)) {
      console.error('Invalid JWT token provided');
      return;
    }
    
    // Only store plaintext in development for debugging
    if (process.env.NODE_ENV === 'development') {
      localStorage.setItem(this.tokenKey, token);
      console.log('🔓 [DEV ONLY] Plaintext token stored for debugging');
    }
    // In production, tokens are only stored encrypted via tokenEncryptionService
  }

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  /**
   * Set refresh token
   * SECURITY: Only stores plaintext in development for debugging
   * Production uses encrypted storage via tokenEncryptionService
   */
  setRefreshToken(refreshToken: string): void {
    // Only store plaintext in development for debugging
    if (process.env.NODE_ENV === 'development') {
      localStorage.setItem(this.refreshTokenKey, refreshToken);
      console.log('🔓 [DEV ONLY] Plaintext refresh token stored for debugging');
    }
    // In production, tokens are only stored encrypted via tokenEncryptionService
  }

  /**
   * Get cached user data
   */
  getCachedUser(): User | null {
    try {
      const userData = localStorage.getItem(this.userKey);
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  }

  /**
   * Set user data
   */
  setUser(user: User): void {
    localStorage.setItem(this.userKey, JSON.stringify(user));
  }

  /**
   * Clear all authentication data including Keycloak instance
   * This clears EVERYTHING: tokens, user data, permissions, menus, caches
   */
  clearAuthData(): void {
    if (process.env.NODE_ENV === 'development') {
      console.log('🧹 Starting COMPLETE auth data cleanup...');
    }

    // 1️⃣ Clear Keycloak instance state (clears tokens from keycloak.js)
    keycloakService.clearAuth();
    
    // 2️⃣ Clear ALL localStorage (tokens, user, permissions, menu)
    const localStorageKeys = [
      this.tokenKey,           // 'auth_token'
      this.refreshTokenKey,    // 'refresh_token'
      this.userKey,            // 'current_user' (includes permissions, menu, roles)
      'user_permissions',      // Permission cache
      'user_menu',             // Menu cache
      'user_profile',          // Profile data
      'app_settings',          // User settings
    ];
    
    localStorageKeys.forEach(key => localStorage.removeItem(key));
    
    // Clear ANY other auth-related keys
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('auth_') || 
          key.startsWith('user_') || 
          key.startsWith('permission_') ||
          key.startsWith('menu_')) {
        localStorage.removeItem(key);
      }
    });
    
    // 3️⃣ Clear ALL sessionStorage (permissions, oauth, menu, user data)
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('permissions_') ||
          key.startsWith('oauth2_') || 
          key === 'oauth2_state' || 
          key === 'oauth2_redirect_uri' ||
          key.startsWith('menu_') || 
          key.startsWith('navigation_') ||
          key.startsWith('user_') || 
          key.startsWith('current_user_')) {
        sessionStorage.removeItem(key);
      }
    });
    
    // 4️⃣ Clear API response cache from httpClient (memory cache)
    apiClient.clearCache();
    
    // 5️⃣ Clear permissions cache explicitly
    this.clearPermissionsCache();
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🧹 Complete cleanup finished:', {
        keycloak: 'cleared',
        localStorage: 'cleared',
        sessionStorage: 'cleared',
        httpClientCache: 'cleared',
        permissionsCache: 'cleared',
        userData: 'cleared',
        menuData: 'cleared'
      });
    }
    
    // 6️⃣ Emit event for any other components to clear their caches
    // This allows menu service, permission service, etc. to clear their internal state
    this.emitAuthCleared();
  }

  /**
   * Handle OAuth2 authentication errors
   */
  async handleOAuth2Error(error: any): Promise<void> {
    const errorMessage = error.message || 'Unknown OAuth2 error';
    
    // Log the error
    await auditLogger.logUserAction('OAUTH2_ERROR', {
      error: errorMessage,
      errorCode: error.code || 'UNKNOWN',
      timestamp: new Date().toISOString(),
      url: window.location.href,
    });

    // Clear authentication data
    this.clearAuthData();

    // Handle specific error types
    if (errorMessage.includes('access_denied')) {
      throw new Error('Access denied. Please contact your administrator.');
    } else if (errorMessage.includes('invalid_request')) {
      throw new Error('Invalid authentication request. Please try again.');
    } else if (errorMessage.includes('server_error')) {
      throw new Error('Authentication server error. Please try again later.');
    } else if (errorMessage.includes('temporarily_unavailable')) {
      throw new Error('Authentication service is temporarily unavailable. Please try again later.');
    } else {
      throw new Error('Authentication failed. Please try again.');
    }
  }

  /**
   * Validate OAuth2 configuration
   */
  validateOAuth2Config(): boolean {
    const keycloakConfig = config.auth.keycloak;
    
    if (!keycloakConfig.issuer) {
      console.error('Keycloak issuer not configured');
      return false;
    }
    
    if (!keycloakConfig.clientId) {
      console.error('Keycloak client ID not configured');
      return false;
    }
    
    if (!keycloakConfig.redirectUri) {
      console.error('Keycloak redirect URI not configured');
      return false;
    }
    
    return true;
  }

  /**
   * Check if token needs refresh
   */
  shouldRefreshToken(): boolean {
    const token = this.getToken();
    if (!token) return false;

    const payload = this.decodeJWT(token);
    if (!payload) return true;

    const now = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = payload.exp - now;
    const refreshThreshold = config.auth.keycloak.tokenRefreshThreshold / 1000;
    
    return timeUntilExpiry < refreshThreshold;
  }

  /**
   * Check if user has access to a specific page
   */
  hasPageAccess(pageIdentifier: string): boolean {
    const user = this.getCachedUser();
    if (!user) return false;

    return user.pages.some((page: any) => page.identifier === pageIdentifier);
  }

  /**
   * Check if user has specific action permission on a page
   */
  hasPageAction(pageIdentifier: string, actionIdentifier: string): boolean {
    const user = this.getCachedUser();
    if (!user) return false;

    const page = user.pages.find((p: any) => p.identifier === pageIdentifier);
    if (!page) return false;

    return page.actions.some((action: any) => action.identifier === actionIdentifier);
  }

  /**
   * Check if user can access specific country
   */
  canAccessCountry(country: string): boolean {
    const user = this.getCachedUser();
    if (!user) return false;

    return user.countries.includes(country) || user.countries.includes('*');
  }

  /**
   * Locales permitted for UI (from Permissions API), with defaults when unset on cached user.
   */
  getAllowedCultures(): string[] {
    return resolveAllowedCultures(this.getCachedUser());
  }

  /**
   * Whether the given locale (e.g. from useIntl / getLocale) is allowed for this user.
   */
  canAccessCulture(locale: string): boolean {
    const user = this.getCachedUser();
    if (!user) return false;
    return localeMatchesAllowedCulture(locale, resolveAllowedCultures(user));
  }

  /**
   * Clear permissions cache (for testing purposes)
   */
  clearPermissionsCacheOnly(): void {
    this.clearPermissionsCache();
    if (process.env.NODE_ENV === 'development') {
      console.log('🔑 AuthService: Permissions cache cleared');
    }
  }

  /**
   * Clear all cached user data (user + permissions)
   * Use this to force fresh data after login/logout
   */
  clearCachedUserData(): void {
    localStorage.removeItem(this.userKey);
    this.clearPermissionsCache();
    if (process.env.NODE_ENV === 'development') {
      console.log('🔑 AuthService: All cached user data cleared');
    }
  }

}

export const authService = new AuthService();
export default authService;

